import {CONFIG} from '../constants';
import {ApolloClient, InMemoryCache, split, HttpLink, FieldPolicy} from '@apollo/client';
import {WebSocketLink} from '@apollo/client/link/ws';
import {getMainDefinition, Reference} from '@apollo/client/utilities';

const httpLink = new HttpLink({
  uri: CONFIG?.THE_GRAPH_GRAPHQL_URI,
});

const wsLink = new WebSocketLink({
  uri: CONFIG?.THE_GRAPH_SUBSCRIPTIONS_URI || '',
  options: {
    reconnect: true,
    timeout: 30000,
    minTimeout: 5000,
  },
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({query}) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink,
);

// https://github.com/apollographql/apollo-client/blob/67fca7d0b98a47abd64d32b0645b55fa7fcdc52a/src/utilities/policies/pagination.ts#L28
// A basic field policy that uses options.args.{offset,limit} to splice
// the incoming data into the existing array. If your arguments are called
// something different (like args.{start,count}), feel free to copy/paste
// this implementation and make the appropriate changes.
function skipFirstPagination<T = Reference>(): FieldPolicy<T[]> {
  return {
    keyArgs: false,
    // @ts-ignore
    merge(existing, incoming, {args}) {
      const merged = existing ? existing.slice(0) : [];
      if (args) {
        // Assume an offset of 0 if args.offset omitted.
        const {skip = 0} = args;
        for (let i = 0; i < incoming.length; ++i) {
          merged[skip + i] = incoming[i];
        }
      } else {
        // It's unusual (probably a mistake) for a paginated field not
        // to receive any arguments, so you might prefer to throw an
        // exception here, instead of recovering by appending incoming
        // onto the existing array.
        // @ts-ignore
        merged.push.apply(merged, incoming);
      }
      return merged;
    },
  };
}

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          artists: skipFirstPagination(),
          arts: skipFirstPagination(),
        },
      },
    },
  }),
});
